/**
 * QuickPropertiesPanel — the 230px mini properties palette (Faz 5.11, OCS
 * quick_view parity): the schema engine's General section for the current
 * selection, rendered in a fixed-width floating card over the viewport.
 * Toggled from the status-bar Quick Properties pill; re-renders with the
 * selection through the same store subscriptions as the full panel.
 */

import { Box, Flex, Text, VStack } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../state/store';
import { undoablePatch } from '../../core/commands/entityOps';
import { buildSections, isCadSchemaEntity } from '../../modules/cad/properties/schema';
import { PropRowView } from '../../modules/cad/properties/PropRowView';

/** OCS quick-view fixed width. */
const QUICK_WIDTH = 230;

export function QuickPropertiesPanel() {
    const { t } = useTranslation();
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entityMap = useAppStore((s) => s.entities);

    const entities = useMemo(
        () => selectedIds.map((id) => entityMap[id]).filter((e) => !!e && isCadSchemaEntity(e)),
        [selectedIds, entityMap],
    );

    const ctx = useMemo(() => ({
        commit: (patch: (entity: import('../../state/store').Entity) => Partial<import('../../state/store').Entity> | null) => {
            undoablePatch(selectedIds, patch);
        },
        vertexIndex: 0,
        setVertexIndex: () => { /* the mini palette shows the General section only */ },
        layers: useAppStore.getState().layers,
    }), [selectedIds]);

    // Quick view = the first schema section (General) — OCS quick_view()
    // renders the same sections at a fixed width.
    const section = useMemo(() => buildSections(entities, ctx)[0], [entities, ctx]);

    return (
        <Box
            position="absolute"
            top="8px"
            right="8px"
            w={`${QUICK_WIDTH}px`}
            maxH="60%"
            overflowY="auto"
            bg="bg.panel"
            border="1px solid"
            borderColor="border.base"
            borderRadius="6px"
            boxShadow="md"
            zIndex={20}
        >
            <Flex align="center" justify="space-between" px={2} py={1} borderBottom="1px solid" borderColor="border.base" flexShrink={0}>
                <Text fontSize="10px" color="text.secondary" textTransform="uppercase" noOfLines={1}>
                    {t('ui.cad.quickProps.title')}
                </Text>
                <Box
                    as="button"
                    aria-label={t('ui.cad.quickProps.close')}
                    onClick={() => useAppStore.getState().toggleQuickProperties()}
                    color="text.secondary"
                    _hover={{ color: 'text.primary' }}
                    display="flex"
                >
                    <X size={12} />
                </Box>
            </Flex>
            <VStack spacing={1.5} align="stretch" p={2} minW={0}>
                {entities.length === 0 ? (
                    <Text fontSize="xs" color="text.secondary">
                        {t('ui.cad.quickProps.noSelection')}
                    </Text>
                ) : (
                    section?.rows.map((row) => <PropRowView key={row.id} row={row} />)
                )}
            </VStack>
        </Box>
    );
}
